import {
	animate,
	state,
	style,
	transition,
	trigger,
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	effect,
	EventEmitter,
	HostListener,
	inject,
	Input,
	OnDestroy,
	OnInit,
	Output
} from '@angular/core';
import { Router } from '@angular/router';
import { UserPreferencesStore } from '@app/shared/stores/user-preferences.store';
import { environment } from '@env/environment';
import { PriceSettingEnum } from '@infrastructure/price-setting.enum';
import { BrowserInfoComponent } from '@modals/browser-info/browser-info.component';
import { DeliveryComponent } from '@modals/delivery/delivery.component';
import { DisplayEnduserModalComponent } from '@modals/display-enduser/display-enduser.component';
import { LegalComponent } from '@modals/legal/legal.component';
import { LogOutComponent } from '@modals/logout.component';
import { ServicePointInfoModalComponent } from '@modals/service-point-info-modal/service-point-info-modal';
import { TermsModalComponent } from '@modals/terms-modal/terms-modal.component';
import { PriceSettingModel } from '@models/price-setting.model';
import { UserDataModel } from '@models/user-data-model';
import { UserTermsDataModel } from '@models/user-terms-data-model';
import { TranslatePipe } from '@ngx-translate/core';
import { EnduserDisplayService } from '@services/enduserdisplay.service';
import { EventsService } from '@services/events.service';
import { ConfigurationMode } from '@services/image.service';
import { LanguageService } from '@services/language.service';
import { ModalService } from '@services/modal.service';
import { NewsService } from '@services/news.service';
import { OptionsDropdownService } from '@services/options-dropdown.service';
import { PriceSettingsService } from '@services/price-settings.service';
import { TermsService } from '@services/terms.service';
import { UserService } from '@services/user.service';
import { AppState } from '@shared/app-state';
import { PermissionConstants } from '@shared/permission-constants';
import { UserTypeConstant } from '@shared/user-type-constants';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

@Component({
	selector: 'options-dropdown',
	templateUrl: 'options-dropdown.component.html',
	styleUrls: ['./options-dropdown.component.scss'],
	standalone: true,
	imports: [CommonModule, TranslatePipe],
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [
		trigger('displayAnim', [
			state(
				'collapsed',
				style({
					// top: "-550px"
					opacity: '0',
				})
			),
			state(
				'expanded',
				style({
					opacity: '1',
				})
			),
			transition('collapsed => expanded', [animate('0.2s')]),
			transition('expanded => collapsed', [animate('0.2s')]),
		]),
	],
})
export class OptionsDropdownComponent implements OnInit, OnDestroy {

	@Input() doShowTerms: boolean;
	@Input() showTermsNotification: boolean;
	@Output() stateChanged: BehaviorSubject<'collapsed' | 'expanded'> = new BehaviorSubject<'collapsed' | 'expanded'>('collapsed');

	public currentMenu: 'container' | 'languages' | 'price' | 'info' = 'container';
	public state: 'collapsed' | 'expanded' = 'collapsed';
	public initExpanded: boolean;
	public currentLanguage: string;
	public currentPriceSetting?: PriceSettingModel;
	public availableLanguages: Array<string>;
	public priceSettings?: Array<PriceSettingModel | undefined>;
	public showdataLoad: boolean = false;
	public showAdminModeToggle: boolean = true;
	public adminModelEnabled: boolean = false;
	public user: UserDataModel | null;
	public enduserDisplayClickedObserveable: Observable<ConfigurationMode | null>;
	public enduserDisplaySubscription: Subscription;
	public displayEnduserModalComponent: any = DisplayEnduserModalComponent;

	private dropdownClickedSubscription: Subscription;
	private dropDownLostFocusSubscription: Subscription;
	private adminModeEnabledSubscription: Subscription;

	private dropdownClickedNotifier: Observable<void>;
	private onDocumentClickEvent: EventEmitter<void> = new EventEmitter();

	configurationStringActivated: boolean = false;
	// showThreeDeeMenuItem: boolean;

	constructor(
		private newsService: NewsService,
		private modalService: ModalService,
		private eventsService: EventsService,
		private optionsDropdownButtonService: OptionsDropdownService,
		private languageService: LanguageService,
		private userService: UserService,
		private router: Router,
		public appState: AppState,
		private priceSettingsService: PriceSettingsService,
		private enduserDisplayService: EnduserDisplayService,
		private termsService: TermsService
	) {
		effect(async () => {
			const cc = this.userPreferencesStore.contactCode();

			if (cc) {
				this.prefsReady = true;

				this.user = await this.userService.GetUser();
				this.priceSettings = this.priceSettingsService.getAllowedPriceSettings(this.user);

				let pd = this.userPreferencesStore.priceDisplay();

				this.currentPriceSetting = this.priceSettings?.find(
					(p) => p?.PriceSetting === pd
				);
			}
		});
	}

	@HostListener('document:click', ['$event']) onStart(event: any) {
		if (!this.initExpanded) {
			this.onDocumentClickEvent.emit();
		} else {
			this.initExpanded = false;
		}
	}

	async ngOnInit() {
		this.user = await this.userService.GetUser();

		this.showdataLoad =
			this.user?.UserPermissions?.some(
				(per) => per == PermissionConstants.CanUseLoadData
			) ?? false;

		this.showAdminModeToggle =
			this.user?.UserPermissions?.some(
				(per) => per == PermissionConstants.IsAdmin
			) ?? false;

		this.dropdownClickedNotifier =
			this.optionsDropdownButtonService.optionsDropdownButtonClickedAsObserveable();

		this.dropdownClickedSubscription =
			this.dropdownClickedNotifier.subscribe(async () => {
				if (this.state == 'collapsed') {
					this.currentMenu = 'container';
					this.state = 'expanded';
					this.initExpanded = true;
					this.priceSettings = await this.initPriceSettingsForCustomer();

					this.stateChanged.next('expanded');
				}
			});

		this.dropDownLostFocusSubscription =
			this.onDocumentClickEvent.subscribe(() => {
				this.state = 'collapsed';
				this.stateChanged.next('collapsed');
			});

		this.adminModeEnabledSubscription =
			this.userService.adminModeEnabled.subscribe((value) => {
				this.adminModelEnabled = value;
			});

		this.currentLanguage = this.languageService.currentLanguage
			? this.languageService.currentLanguage
			: this.languageService.defaultLanguage;

		this.availableLanguages = this.languageService.getLanguages();

		this.enduserDisplayClickedObserveable = this.enduserDisplayService.enduserDisplayClickedObservable();

		// this.showThreeDeeMenuItem = (this.user?.Type === UserTypeConstant.Employee) || (this.user?.Type === UserTypeConstant.Consultant);

		this.priceSettings = this.priceSettingsService.getAllowedPriceSettings(this.user);

		this.currentPriceSetting = this.priceSettings.find(
			(p) => p?.PriceSetting === PriceSettingEnum.AllowNone
		);
	}

	prefsReady = false;

	ngOnDestroy() {
		if (this.dropdownClickedSubscription) {
			this.dropdownClickedSubscription.unsubscribe();
		}

		if (this.dropDownLostFocusSubscription) {
			this.dropDownLostFocusSubscription.unsubscribe();
		}

		if (this.adminModeEnabledSubscription) {
			this.adminModeEnabledSubscription.unsubscribe();
		}

		if (this.enduserDisplaySubscription) {
			this.enduserDisplaySubscription.unsubscribe();
		}
	}

	closeMenu() {
		this.optionsDropdownButtonService.toggleDropdownState();
	}

	public toggleGeneralInfoMenu() {
		this.initExpanded = true;
		this.currentMenu = this.currentMenu === 'info' ? 'container' : 'info';
	}

	public toggleLanguagesMenu() {
		this.initExpanded = true;
		this.currentMenu = this.currentMenu === 'languages' ? 'container' : 'languages';
	}

	public togglePriceSettingsMenu() {
		this.initExpanded = true;
		this.currentMenu = this.currentMenu === 'price' ? 'container' : 'price';
	}

	public showDeliveryModal() {
		this.modalService.create(DeliveryComponent);
	}

	public showLegalModal() {
		this.newsService.getLegalNews().then((res) => {
			const newsJson = res;
			newsJson.content = newsJson.content
				.replace(/<strong>/gi, '<span class="font-medium">')
				.replace(/strong/gi, 'span');

			this.modalService.create(LegalComponent, {
				content: newsJson,
			});

			this.eventsService.showLoader(false);
		});
	}

	public showServicePointInfoModal() {
		this.modalService.create(ServicePointInfoModalComponent);
	}

	public async showTermsModal() {
		let termsData = await this.termsService.getUserTermsSignedData(
			this.user?.CustomerNo
		);
		this.modalService.create<TermsModalComponent>(TermsModalComponent, {
			termsData: new UserTermsDataModel(
				termsData.TermsSigned,
				termsData.TermsDocumentLegal
			),
			contactCode: this.user?.ContactCode,
		});
	}

	public showEnduserDisplayModal() {
		this.modalService.create(DisplayEnduserModalComponent, {
			content: ''
		});
	}

	public showSupportModal() {
		this.modalService.create(BrowserInfoComponent);
		this.eventsService.showLoader(false);
	}

	public showFileMakerAppsMenuItem() {
		if (this.user !== null && this.user?.canSeeFileMakerApps) {
			return true;
		}

		return false;
	}

	showFileMakerApps() {
		window.open(environment.fileMakerAppsUrl, '_blank');
	}

	public ShowLanguages() {
		this.initExpanded = true;
		this.currentMenu =
			this.currentMenu === 'languages' ? 'container' : 'languages';
	}




	public doShowTermsMenuItem() {
		if (
			this.user !== null &&
			this.user?.Type === UserTypeConstant.Customer
		) {
			return true;
		}

		return false;
	}

	public async changeLanguage(language: string, event: MouseEvent) {
		if (language === this.languageService.currentLanguage) {
			event.stopPropagation();
		} else {
			await this.languageService.useLanguage(language);
			this.currentLanguage = language;
		}
	}

	public gotoOrderInfo() {
		this.router.navigate(['/bag', 'order-info']);
	}

	public async initPriceSettingsForCustomer(): Promise<Array<PriceSettingModel | undefined> | undefined> {
		if (!this.appState.priceModuleEnabled.getValue()) {
			return;
		}

		const user = this.userService.currentUser$.value;

		let priceSettings = this.priceSettingsService.getAllowedPriceSettings(user);

		let pd = this.userPreferencesStore.priceDisplay();

		this.currentPriceSetting = priceSettings.find(
			(p) => p?.PriceSetting === pd
		);

		return priceSettings;
	}

	public async changePriceSetting(
		priceSetting: PriceSettingModel | undefined,
		event: MouseEvent | null = null
	) {
		if (priceSetting === this.currentPriceSetting) {
			event?.stopPropagation();
		}
		else {
			const ps = priceSetting?.PriceSetting ?? PriceSettingEnum.AllowNone;

			this.userPreferencesStore.setPriceDisplay(ps);

			this.appState.priceSetting.next(
				priceSetting ?? new PriceSettingModel(PriceSettingEnum.AllowNone, '')
			);

			this.onDocumentClickEvent.emit();
		}
	}

	public async goToServiceManager() {
		this.router.navigate(['administration']);
	}

	userPreferencesStore = inject(UserPreferencesStore);

	public toggleAdminMode() {
		this.userService.adminModeToggle();
	}

	toggleConfigurationStringState() {
		this.userPreferencesStore.toggleConfigurationStringActive();
	}

	public showLogOutModal() {
		this.modalService.create(LogOutComponent);
		this.eventsService.showLoader(false);
	}

}
